import { EventStatus, MarketStatus, Language } from '@finbackoffice/enums';
import EventEmitter from 'events';

const CLIENT_CONNECTED = "connected";
const CLIENT_DISCONNECTED = "disconnected";
const CLIENT_FEED_PREMATCH = "prematch";
const CLIENT_FEED_LIVE = "live";
const CLIENT_SUBSCRIBE_EVENT = "event";
const CLIENT_SUBSCRIBE_TOURNAMENT = "tournament";
const CLIENT_SUBSCRIBE_MARKET = "market";
const CLIENT_CALENDAR = "calendar";
const CLIENT_CALENDAR_TREE = "calendar_tree";
const CLIENT_WITHDRAWAL = "withdrawal";
const CLIENT_WITHDRAWAL_BATCH = "withdrawal_batch";
const CLIENT_DEPOSIT = "deposit";
const CLIENT_DEPOSIT_BATCH = "deposit_batch";
const CLIENT_TICKET = "ticket";
const CLIENT_WALLET = "wallet";
const CLIENT_CHAT_MESSAGE = "chat_message";
const CLIENT_EXCHANGE_RATES_LATEST = "exchange_rates_latest";
const CLIENT_EXCHANGE_RATES_TIME = "exchange_rates_time";
const CLIENT_TOP_GAMES = "top";
const CLIENT_UPCOMING_GAMES = "upcoming";
const CLIENT_PING = "ping";
const SERVER_UNSUBSCRIBE = "unsubscribe";
const SERVER_CONNECT = "connect";
const SERVER_CONNECTED = "connected";
const SERVER_FEED_PREMATCH = "prematch";
const SERVER_FEED_LIVE = "live";
const SERVER_GAME_EVENT = "event";
const SERVER_TOURNAMENT = "tournament";
const SERVER_MARKET = "market";
const SERVER_CALENDAR = "calendar";
const SERVER_WITHDRAWAL = "withdrawal";
const SERVER_WITHDRAWAL_BATCH = "withdrawal_batch";
const SERVER_DEPOSIT = "deposit";
const SERVER_TICKET = "ticket";
const SERVER_WALLET = "wallet";
const SERVER_CHAT_MESSAGE = "chat_message";
const SERVER_EXCHANGE_RATES_LATEST = "exchange_rates_latest";
const SERVER_EXCHANGE_RATES_TIME = "exchange_rates_time";
const SERVER_TOP_GAMES = "top";
const SERVER_UPCOMING_GAMES = "upcoming";
const SERVER_PING = "ping";
const SERVER_PONG = "pong";

class ClientsService {
    constructor() {
        this.eventEmitter = new EventEmitter();
    }
    subscribe(rid, handler) {
        this.eventEmitter.on(rid, handler);
    }
    unsubscribe(rid, handler) {
        this.eventEmitter.off(rid, handler);
    }
    sendClientMessage(rid, data, error) {
        if (error) {
            this.eventEmitter.emit(rid, undefined, error);
        }
        else {
            this.eventEmitter.emit(rid, data);
        }
    }
    removeAllListeners() {
        this.eventEmitter.eventNames().forEach((eventName) => {
            if ([CLIENT_CONNECTED, CLIENT_DISCONNECTED].indexOf(eventName) === -1) {
                this.eventEmitter.removeAllListeners(eventName);
            }
        });
    }
}

const PING_INTERVAL = 30000;
const RECONNECT_INTERVAL = 1000;
class WebsocketAdapter {
    constructor(options) {
        this.options = options;
        this.createSocket = () => {
            this.ws = new WebSocket(this.options.url);
            this.ws.addEventListener("open", this.handleOpen);
            this.ws.addEventListener("error", this.reconnect);
            this.ws.addEventListener("close", this.reconnect);
            this.ws.addEventListener("message", this.handleMessage);
        };
        this.destroy = () => {
            clearInterval(this.pingTimeout);
            clearTimeout(this.reconnectTimeout);
            this.ws.removeEventListener("open", this.handleOpen);
            this.ws.removeEventListener("error", this.reconnect);
            this.ws.removeEventListener("close", this.reconnect);
            this.ws.removeEventListener("message", this.handleMessage);
            this.ws.close();
        };
        this.handleOpen = () => {
            if (this.options.shouldPing) {
                this.pingTimeout = setInterval(this.options.onPing, PING_INTERVAL);
            }
            this.options.onOpen();
        };
        this.reconnect = () => {
            this.options.onClose();
            this.destroy();
            this.reconnectTimeout = setTimeout(this.createSocket, RECONNECT_INTERVAL);
        };
        this.send = (message) => {
            if (!this.ws) {
                console.error("Unable to send a message, socket is not defined");
                return;
            }
            this.ws.send(JSON.stringify(message));
        };
        this.handleMessage = (response) => {
            const message = JSON.parse(response.data);
            if (message.event === "error") {
                console.error("Socket error:", message);
                this.options.onMessageError(message);
                return;
            }
            this.options.onMessage(message);
        };
        this.isOpen = () => {
            return this.ws.readyState === this.ws.OPEN;
        };
        this.createSocket();
    }
}

class SocketService {
    constructor(options) {
        this.options = options;
        this.ws = new WebsocketAdapter({
            url: options.url,
            shouldPing: options.shouldPing,
            onOpen: this.options.onOpen,
            onClose: this.options.onClose,
            onMessageError: this.options.onMessageError,
            onMessage: this.options.onMessage,
            onPing: () => {
                this.ws.send({
                    event: SERVER_PING,
                    data: { timestamp: new Date().getTime(), rid: "", token: this.options.token },
                });
            },
        });
    }
    sendMessage(message) {
        this.ws.send(message);
    }
    startSubscription(rid, event, data) {
        if (!this.ws.isOpen()) {
            return;
        }
        this.sendMessage({
            event,
            data: Object.assign(Object.assign({ token: this.options.token }, data), { rid }),
        });
    }
    endSubscription(rid) {
        if (!this.ws.isOpen()) {
            return;
        }
        this.sendMessage({
            event: SERVER_UNSUBSCRIBE,
            data: {
                rid,
                token: this.options.token,
            },
        });
    }
    destroy() {
        return this.ws.destroy();
    }
}

class FeedStorage {
    constructor(STATUSES_TO_DELETE, debug = false) {
        this.STATUSES_TO_DELETE = STATUSES_TO_DELETE;
        this.debug = debug;
        this.SPORTS = new Map();
        this.CATEGORIES = new Map();
        this.TOURNAMENTS = new Map();
        this.EVENTS = new Map();
    }
    warn(message) {
        if (this.debug) {
            console.warn(message);
        }
    }
    update(response) {
        this.updateSports(response.sports);
    }
    getSports(options) {
        const sports = Array.from(this.SPORTS.values());
        if (options === null || options === undefined ? undefined : options.include_empty) {
            return sports;
        }
        return sports.filter((s) => s.games_count > 0);
    }
    getSport(id) {
        return this.SPORTS.get(id);
    }
    getCategories(sportId, options) {
        const sport = this.SPORTS.get(sportId);
        const categories = [];
        const include_empty = !!(options === null || options === undefined ? undefined : options.include_empty);
        if (!sport) {
            this.warn(`Unable to find sport with id ${sportId}`);
            return [];
        }
        for (const category_id of sport.categories) {
            const category = this.CATEGORIES.get(category_id);
            if (!category) {
                this.warn(`Unable to find category with id ${category_id}`);
                continue;
            }
            if (include_empty || category.games_count > 0) {
                categories.push(category);
            }
        }
        return categories;
    }
    getCategory(id) {
        return this.CATEGORIES.get(id);
    }
    getTournaments(categoryId, options) {
        const category = this.CATEGORIES.get(categoryId);
        const tournaments = [];
        const include_empty = !!(options === null || options === undefined ? undefined : options.include_empty);
        if (!category) {
            this.warn(`Unable to find category with id ${categoryId}`);
            return [];
        }
        for (const tournament_id of category.tournaments) {
            const tournament = this.TOURNAMENTS.get(tournament_id);
            if (!tournament) {
                this.warn(`Unable to find tournament with id ${tournament_id}`);
                continue;
            }
            if (include_empty || tournament.games_count > 0) {
                tournaments.push(tournament);
            }
        }
        return tournaments;
    }
    getTopLeagues() {
        const topLeagues = [];
        for (const sport of this.SPORTS.values()) {
            for (const category_id of sport.categories) {
                const category = this.CATEGORIES.get(category_id);
                if (!category) {
                    continue;
                }
                for (const tournament_id of category.tournaments) {
                    const tournament = this.TOURNAMENTS.get(tournament_id);
                    if (tournament && tournament.is_top && tournament.games_count) {
                        topLeagues.push(Object.assign(Object.assign({}, tournament), { sport_slug: sport.slug, sport_name: sport.name, sport_id: sport.id, category_id: category.id, category_name: category.name }));
                    }
                }
            }
        }
        return topLeagues.sort((a, b) => a.top_index - b.top_index);
    }
    getTournament(id) {
        return this.TOURNAMENTS.get(id);
    }
    getEvents(tournamentId, options) {
        const tournament = this.TOURNAMENTS.get(tournamentId);
        const events = [];
        const statuses_to_exclude = (options === null || options === undefined ? undefined : options.statuses_to_exclude) || [EventStatus.Settled];
        if (!tournament) {
            this.warn(`Unable to find tournament with id ${tournamentId}`);
            return [];
        }
        for (const event_id of tournament.events) {
            const event = this.EVENTS.get(event_id);
            if (!event) {
                this.warn(`Unable to find event with id ${event_id}`);
                continue;
            }
            if (statuses_to_exclude.indexOf(event.status) === -1) {
                events.push(event);
            }
        }
        return events;
    }
    getEventsBySportId(id, options) {
        const sport = this.SPORTS.get(id);
        const statuses_to_exclude = (options === null || options === undefined ? undefined : options.statuses_to_exclude) || [EventStatus.Settled];
        const results = [];
        if (!sport) {
            return results;
        }
        for (const categoryId of sport.categories) {
            const category = this.CATEGORIES.get(categoryId);
            if (!category) {
                continue;
            }
            for (const tournamentId of category.tournaments) {
                const tournament = this.TOURNAMENTS.get(tournamentId);
                if (!tournament) {
                    continue;
                }
                for (const eventId of tournament.events) {
                    const event = this.EVENTS.get(eventId);
                    if (!event) {
                        continue;
                    }
                    if (statuses_to_exclude.indexOf(event.status) === -1) {
                        results.push(event);
                    }
                }
            }
        }
        return results;
    }
    getEvent(id) {
        return this.EVENTS.get(id);
    }
    clear() {
        this.SPORTS.clear();
        this.TOURNAMENTS.clear();
        this.CATEGORIES.clear();
        this.EVENTS.clear();
    }
    updateSports(sports) {
        sports.forEach((s) => {
            var _a;
            const sport = this.SPORTS.get(s.id);
            const categories = [...new Set([...((_a = sport === null || sport === undefined ? undefined : sport.categories) !== null && _a !== undefined ? _a : []), ...s.categories.map((c) => c.id)])];
            this.SPORTS.set(s.id, Object.assign(Object.assign({}, s), { categories }));
            this.updateCategories(s.categories, s.id);
        });
    }
    updateCategories(categories, sport_id) {
        categories.forEach((c) => {
            var _a;
            const category = this.CATEGORIES.get(c.id);
            const tournaments = [...new Set([...((_a = category === null || category === undefined ? undefined : category.tournaments) !== null && _a !== undefined ? _a : []), ...c.tournaments.map((t) => t.id)])];
            this.CATEGORIES.set(c.id, Object.assign(Object.assign({}, c), { tournaments }));
            this.updateTournaments(c.tournaments, sport_id, c.id);
        });
    }
    updateTournaments(tournaments, sport_id, category_id) {
        tournaments.forEach((t) => {
            var _a;
            const tournament = this.TOURNAMENTS.get(t.id);
            const events = [...new Set([...((_a = tournament === null || tournament === undefined ? undefined : tournament.events) !== null && _a !== undefined ? _a : []), ...t.events.map((e) => e.id)])];
            this.TOURNAMENTS.set(t.id, Object.assign(Object.assign({}, t), { events }));
            this.updateEvents(t.events, sport_id, category_id, t.id);
        });
    }
    updateEvents(events, sport_id, category_id, tournament_id) {
        events.forEach((event) => {
            var _a, _b;
            if (this.STATUSES_TO_DELETE.indexOf(event.status) !== -1) {
                const tournament = this.TOURNAMENTS.get(tournament_id);
                if (!tournament) {
                    this.warn(`Unable to find tournament with id ${tournament_id}`);
                    return;
                }
                this.TOURNAMENTS.set(tournament.id, Object.assign(Object.assign({}, tournament), { events: tournament.events.filter((event_id) => event_id !== event.id) }));
                this.EVENTS.delete(event.id);
                return;
            }
            if (!this.EVENTS.has(event.id)) {
                const sport = this.SPORTS.get(sport_id);
                const category = this.CATEGORIES.get(category_id);
                const tournament = this.TOURNAMENTS.get(tournament_id);
                this.EVENTS.set(event.id, Object.assign(Object.assign({}, event), { sport_id: sport.id, sport_name: sport.name, sport_slug: sport.slug, category_id: category.id, category_name: category.name, tournament_id: tournament.id, tournament_name: tournament.name, tournament_is_top: (_a = tournament === null || tournament === undefined ? undefined : tournament.is_top) !== null && _a !== undefined ? _a : false, tournament_top_index: (_b = tournament === null || tournament === undefined ? undefined : tournament.top_index) !== null && _b !== undefined ? _b : 0 }));
                return;
            }
            if (this.EVENTS.has(event.id)) {
                const cache = this.EVENTS.get(event.id);
                const markets = this.mergeEventMarkets(cache.markets, event.markets);
                this.EVENTS.set(event.id, Object.assign(Object.assign(Object.assign({}, cache), event), { markets }));
                return;
            }
        });
    }
    mergeEventMarkets(marketsMergeInto, marketsMergeFrom) {
        const storedMarkets = [...marketsMergeInto];
        for (const market of marketsMergeFrom) {
            const index = storedMarkets.findIndex((m) => m.id === market.id);
            if (index === -1 && market.status === MarketStatus.Open) {
                storedMarkets.push(market);
                continue;
            }
            if (index !== -1 && market.status === MarketStatus.Open) {
                storedMarkets.splice(index, 1, market);
                continue;
            }
            if (index !== -1 && market.status !== MarketStatus.Open) {
                storedMarkets.splice(index, 1);
                continue;
            }
        }
        return storedMarkets;
    }
}

const getRandomRequestId = (length = 20) => {
    let result = "";
    const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

class SubscriptionStorage {
    constructor() {
        this.storageMap = new Map();
    }
    findRidByEvent(eventName, data) {
        const handlerId = this.getHandlerId({ eventName, data });
        return this.findRidByHandlerId(handlerId);
    }
    isSubscriptionExist(rid) {
        for (const [key] of this.storageMap) {
            if (key.rid === rid) {
                return true;
            }
        }
        return false;
    }
    getEventNameByRid(rid) {
        for (const [key] of this.storageMap) {
            if (key.rid === rid) {
                return key.eventName;
            }
        }
    }
    createSubscription(params) {
        const handlerId = this.getHandlerId({ eventName: params.eventName, data: params.data });
        const rid = getRandomRequestId();
        this.storageMap.set({ rid: rid, handlerId, eventName: params.eventName }, [params.handler]);
        return rid;
    }
    addHandlerToSubscription(rid, handler) {
        for (const [key, handlers] of this.storageMap) {
            if (key.rid === rid) {
                this.storageMap.set(key, [...handlers, handler]);
                return;
            }
        }
    }
    removeHandlerFromSubscription(rid, handler) {
        for (const [key, handlers] of this.storageMap) {
            if (key.rid === rid) {
                const handlersFiltered = handlers.filter((h) => h !== handler);
                if (handlersFiltered.length === 0) {
                    this.storageMap.delete(key);
                }
                else {
                    this.storageMap.set(key, handlersFiltered);
                }
                return;
            }
        }
    }
    clear() {
        this.storageMap.clear();
    }
    findRidByHandler(handler) {
        for (const [key, handlers] of this.storageMap) {
            if (handlers.find((h) => h === handler)) {
                return key.rid;
            }
        }
    }
    findRidByHandlerId(handlerId) {
        for (const key of this.storageMap.keys()) {
            if (key.handlerId === handlerId) {
                return key.rid;
            }
        }
    }
    getHandlerId(structure) {
        return JSON.stringify(structure);
    }
}

class EventHandlingStrategy {
    constructor(clientsService, socketService) {
        this.clientsService = clientsService;
        this.socketService = socketService;
        this.subscriptionsStorage = new SubscriptionStorage();
    }
    on(event, handler, ...payload) {
        let rid = this.subscriptionsStorage.findRidByEvent(event, payload[0]);
        if (rid) {
            this.subscriptionsStorage.addHandlerToSubscription(rid, handler);
        }
        else {
            rid = this.subscriptionsStorage.createSubscription({ eventName: event, data: payload[0], handler });
            this.socketService.startSubscription(rid, event, payload[0]);
        }
        this.clientsService.subscribe(rid, handler);
    }
    off(event, handler) {
        const rid = this.subscriptionsStorage.findRidByHandler(handler);
        if (!rid) {
            return;
        }
        this.clientsService.unsubscribe(rid, handler);
        this.subscriptionsStorage.removeHandlerFromSubscription(rid, handler);
        if (!this.subscriptionsStorage.isSubscriptionExist(rid)) {
            this.socketService.endSubscription(rid);
        }
    }
}

class DefaultStrategy extends EventHandlingStrategy {
    constructor(clientsService, socketService) {
        super(clientsService, socketService);
    }
    static getEvents() {
        return [
            [CLIENT_DEPOSIT, SERVER_DEPOSIT],
            [CLIENT_DEPOSIT_BATCH, CLIENT_DEPOSIT_BATCH],
            [CLIENT_WITHDRAWAL, SERVER_WITHDRAWAL],
            [CLIENT_WITHDRAWAL_BATCH, SERVER_WITHDRAWAL_BATCH],
            [CLIENT_EXCHANGE_RATES_LATEST, SERVER_EXCHANGE_RATES_LATEST],
            [CLIENT_EXCHANGE_RATES_TIME, SERVER_EXCHANGE_RATES_TIME],
            [CLIENT_TICKET, SERVER_TICKET],
            [CLIENT_CHAT_MESSAGE, SERVER_CHAT_MESSAGE],
            [CLIENT_WALLET, SERVER_WALLET],
            [CLIENT_SUBSCRIBE_MARKET, SERVER_MARKET],
            [CLIENT_SUBSCRIBE_EVENT, SERVER_GAME_EVENT],
            [CLIENT_SUBSCRIBE_TOURNAMENT, SERVER_TOURNAMENT],
            [CLIENT_CALENDAR, SERVER_CALENDAR],
            [CLIENT_TOP_GAMES, SERVER_TOP_GAMES],
            [CLIENT_UPCOMING_GAMES, SERVER_UPCOMING_GAMES],
        ];
    }
    getEventsFromFeedResponse(response) {
        const events = [];
        for (const sport of response.sports) {
            for (const category of sport.categories) {
                for (const tournament of category.tournaments) {
                    for (const event of tournament.events) {
                        events.push(Object.assign(Object.assign({}, event), { sport_id: sport.id, sport_name: sport.name, sport_slug: sport.slug, category_id: category.id, category_name: category.name, tournament_id: tournament.id, tournament_name: tournament.name, tournament_is_top: tournament.is_top, tournament_top_index: tournament.top_index }));
                    }
                }
            }
        }
        return events;
    }
    getDataFromResponse(clientEvent, response) {
        switch (clientEvent) {
            case CLIENT_SUBSCRIBE_EVENT:
                const events = this.getEventsFromFeedResponse(response.data);
                return events[0];
            case CLIENT_SUBSCRIBE_TOURNAMENT:
            case CLIENT_TOP_GAMES:
            case CLIENT_UPCOMING_GAMES:
            case CLIENT_CALENDAR:
                return this.getEventsFromFeedResponse(response.data);
            default:
                return response.data;
        }
    }
    handleServerMessage(response) {
        if (!this.subscriptionsStorage.isSubscriptionExist(response.rid)) {
            return;
        }
        const clientEventName = this.subscriptionsStorage.getEventNameByRid(response.rid);
        const data = this.getDataFromResponse(clientEventName, response);
        this.clientsService.sendClientMessage(response.rid, data);
    }
}

class SystemStrategy extends EventHandlingStrategy {
    constructor(clientsService, socketService) {
        super(clientsService, socketService);
        this.rid = "";
    }
    static getEvents() {
        return [
            [CLIENT_CONNECTED, SERVER_CONNECTED],
            [CLIENT_DISCONNECTED, null],
            [CLIENT_PING, SERVER_PONG],
            [null, SERVER_UNSUBSCRIBE],
        ];
    }
    sendConnectSystemMessage(token, language) {
        this.rid = getRandomRequestId();
        this.socketService.startSubscription(this.rid, SERVER_CONNECT, {
            token: token,
            language: language,
        });
    }
    on(event, handler, ...payload) {
        this.clientsService.subscribe(event, handler);
    }
    off(event, handler) {
        this.clientsService.unsubscribe(event, handler);
    }
    handleServerMessage(message) {
        switch (message.event) {
            case SERVER_PONG:
                this.clientsService.sendClientMessage(CLIENT_PING, message.data);
                break;
            case SERVER_UNSUBSCRIBE:
                break;
            case SERVER_CONNECTED:
                message = message;
                if (message.rid !== this.rid) {
                    return;
                }
                if (message.event === SERVER_CONNECTED) {
                    this.clientsService.sendClientMessage(CLIENT_CONNECTED, message.data);
                }
                break;
        }
    }
}

class FeedStrategy extends EventHandlingStrategy {
    constructor(clientsService, socketService, statuses_to_delete) {
        super(clientsService, socketService);
        this.liveFeedStorage = new FeedStorage(statuses_to_delete);
        this.prematchFeedStorage = new FeedStorage(statuses_to_delete);
        this.resultsFeedStorage = new FeedStorage(statuses_to_delete);
    }
    static getEvents() {
        return [
            [CLIENT_FEED_LIVE, SERVER_FEED_LIVE],
            [CLIENT_FEED_PREMATCH, SERVER_FEED_PREMATCH],
            [CLIENT_CALENDAR_TREE, SERVER_CALENDAR],
        ];
    }
    on(event, handler, ...payload) {
        event = this.fromIncomingEventName(event);
        super.on(event, handler, ...payload);
    }
    off(event, handler) {
        event = this.fromIncomingEventName(event);
        this.clearStorage(event);
        super.off(event, handler);
    }
    clear() {
        this.clearStorage(SERVER_FEED_PREMATCH);
        this.clearStorage(SERVER_FEED_LIVE);
    }
    handleServerMessage(message) {
        if (!this.subscriptionsStorage.isSubscriptionExist(message.rid)) {
            return;
        }
        switch (message.event) {
            case SERVER_FEED_LIVE:
                this.liveFeedStorage.update(message.data);
                this.clientsService.sendClientMessage(message.rid, undefined);
                return;
            case SERVER_FEED_PREMATCH:
                this.prematchFeedStorage.update(message.data);
                this.clientsService.sendClientMessage(message.rid, undefined);
                return;
            case SERVER_CALENDAR:
                this.resultsFeedStorage.update(message.data);
                this.clientsService.sendClientMessage(message.rid, undefined);
                return;
        }
    }
    getLiveSports(options) {
        return this.liveFeedStorage.getSports(options);
    }
    getLiveCategories(sportId, options) {
        return this.liveFeedStorage.getCategories(sportId, options);
    }
    getLiveTournaments(categoryId, options) {
        return this.liveFeedStorage.getTournaments(categoryId, options);
    }
    getLiveEvents(tournamentId, options) {
        return this.liveFeedStorage.getEvents(tournamentId, options);
    }
    getLiveSport(id) {
        return this.liveFeedStorage.getSport(id);
    }
    getLiveCategory(id) {
        return this.liveFeedStorage.getCategory(id);
    }
    getLiveTournament(id) {
        return this.liveFeedStorage.getTournament(id);
    }
    getLiveEvent(id) {
        return this.liveFeedStorage.getEvent(id);
    }
    getLiveTopLeagues() {
        return this.liveFeedStorage.getTopLeagues();
    }
    getPrematchSports(options) {
        return this.prematchFeedStorage.getSports(options);
    }
    getPrematchCategories(sportId, options) {
        return this.prematchFeedStorage.getCategories(sportId, options);
    }
    getPrematchTournaments(categoryId, options) {
        return this.prematchFeedStorage.getTournaments(categoryId, options);
    }
    getPrematchSport(id) {
        return this.prematchFeedStorage.getSport(id);
    }
    getPrematchCategory(id) {
        return this.prematchFeedStorage.getCategory(id);
    }
    getPrematchTournament(id) {
        return this.prematchFeedStorage.getTournament(id);
    }
    getPrematchTopLeagues() {
        return this.prematchFeedStorage.getTopLeagues();
    }
    getPrematchEventsBySportId(id) {
        return this.prematchFeedStorage.getEventsBySportId(id);
    }
    getResultsSports(options) {
        return this.resultsFeedStorage.getSports(options);
    }
    getResultsCategories(sportId, options) {
        return this.resultsFeedStorage.getCategories(sportId, options);
    }
    getResultsTournaments(categoryId, options) {
        return this.resultsFeedStorage.getTournaments(categoryId, options);
    }
    getResultsSport(id) {
        return this.resultsFeedStorage.getSport(id);
    }
    getResultsEvents(tournamentId, options) {
        return this.resultsFeedStorage.getEvents(tournamentId, options || { statuses_to_exclude: [] });
    }
    mergeEventMarkets(marketsMergeInto, marketsMergeFrom) {
        return this.liveFeedStorage.mergeEventMarkets(marketsMergeInto, marketsMergeFrom);
    }
    clearStorage(event) {
        if (event === CLIENT_FEED_PREMATCH) {
            this.prematchFeedStorage.clear();
            return;
        }
        if (event === CLIENT_FEED_LIVE) {
            this.liveFeedStorage.clear();
            return;
        }
        if (event === CLIENT_CALENDAR) {
            this.resultsFeedStorage.clear();
            return;
        }
    }
    fromIncomingEventName(event) {
        if (event === CLIENT_CALENDAR_TREE) {
            event = CLIENT_CALENDAR;
        }
        return event;
    }
}

class HandlingStrategyFactory {
    constructor(clientsService, socketService, statuses_to_delete) {
        this.clientsService = clientsService;
        this.socketService = socketService;
        this.statuses_to_delete = statuses_to_delete;
        this.strategies = new Map();
        this.strategies.set(DefaultStrategy.getEvents(), new DefaultStrategy(this.clientsService, this.socketService));
        this.strategies.set(SystemStrategy.getEvents(), new SystemStrategy(this.clientsService, this.socketService));
        this.strategies.set(FeedStrategy.getEvents(), new FeedStrategy(this.clientsService, this.socketService, statuses_to_delete));
    }
    getStrategyByClientName(eventName) {
        for (const strategy of this.strategies) {
            if (strategy[0].some(([clientEvent]) => clientEvent === eventName)) {
                return strategy[1];
            }
        }
        throw new Error(`Strategy for event ${eventName} not found`);
    }
    getStrategiesByServerName(eventName) {
        const strategies = [];
        for (const [events, strategy] of this.strategies) {
            if (events.some(([, serverEvent]) => serverEvent === eventName)) {
                strategies.push(strategy);
            }
        }
        if (strategies.length === 0) {
            throw new Error(`Strategies for event ${eventName} not found`);
        }
        else {
            return strategies;
        }
    }
    getStrategyOfType(strategyType) {
        for (const [, strategy] of this.strategies) {
            if (strategy instanceof strategyType) {
                return strategy;
            }
        }
        throw new Error(`${strategyType.name} not found`);
    }
    getSystemStrategy() {
        return this.getStrategyOfType(SystemStrategy);
    }
    getFeedStrategy() {
        return this.getStrategyOfType(FeedStrategy);
    }
}

class WebsocketClient {
    constructor(token, url, statuses_to_delete = [EventStatus.Ended], language = Language.en, shouldPing = true, pingInterval = 1000) {
        this.token = token;
        this.url = url;
        this.language = language;
        this.shouldPing = shouldPing;
        this.pingInterval = pingInterval;
        this.handleServerMessages = (message) => {
            const strategies = this.handlingStrategyFactory.getStrategiesByServerName(message.event);
            for (const strategy of strategies) {
                strategy.handleServerMessage(message);
            }
        };
        this.client = new ClientsService();
        this.socket = new SocketService({
            url: this.url,
            token: this.token,
            shouldPing: this.shouldPing,
            onOpen: () => {
                this.system.sendConnectSystemMessage(this.token, this.language);
            },
            onClose: () => {
                this.feed.clear();
                this.client.sendClientMessage(CLIENT_DISCONNECTED, undefined);
            },
            onMessageError: (message) => {
                if (message.event === "error") {
                    this.client.sendClientMessage(message.rid, undefined, message.data);
                }
            },
            onMessage: this.handleServerMessages,
        });
        this.handlingStrategyFactory = new HandlingStrategyFactory(this.client, this.socket, statuses_to_delete);
        this.feed = this.handlingStrategyFactory.getFeedStrategy();
        this.system = this.handlingStrategyFactory.getSystemStrategy();
    }
    destroy() {
        this.feed.clear();
        this.client.removeAllListeners();
        this.socket.destroy();
    }
    on(event, handler, ...payload) {
        const strategy = this.handlingStrategyFactory.getStrategyByClientName(event);
        strategy.on(event, handler, ...payload);
    }
    off(event, handler) {
        const strategy = this.handlingStrategyFactory.getStrategyByClientName(event);
        strategy.off(event, handler);
    }
    getLiveSports(options) {
        return this.feed.getLiveSports(options);
    }
    getLiveCategories(sportId, options) {
        return this.feed.getLiveCategories(sportId, options);
    }
    getLiveTournaments(categoryId, options) {
        return this.feed.getLiveTournaments(categoryId, options);
    }
    getLiveEvents(tournamentId, options) {
        return this.feed.getLiveEvents(tournamentId, options);
    }
    getLiveSport(id) {
        return this.feed.getLiveSport(id);
    }
    getLiveCategory(id) {
        return this.feed.getLiveCategory(id);
    }
    getLiveTournament(id) {
        return this.feed.getLiveTournament(id);
    }
    getLiveEvent(id) {
        return this.feed.getLiveEvent(id);
    }
    getPrematchSports(options) {
        return this.feed.getPrematchSports(options);
    }
    getPrematchCategories(sportId, options) {
        return this.feed.getPrematchCategories(sportId, options);
    }
    getPrematchTournaments(categoryId, options) {
        return this.feed.getPrematchTournaments(categoryId, options);
    }
    getPrematchSport(id) {
        return this.feed.getPrematchSport(id);
    }
    getPrematchCategory(id) {
        return this.feed.getPrematchCategory(id);
    }
    getPrematchTournament(id) {
        return this.feed.getPrematchTournament(id);
    }
    getPrematchTopLeagues() {
        return this.feed.getPrematchTopLeagues();
    }
    getPrematchEventsBySportId(id) {
        return this.feed.getPrematchEventsBySportId(id);
    }
    getLiveTopLeagues() {
        return this.feed.getLiveTopLeagues();
    }
    getResultsSports(options) {
        return this.feed.getResultsSports(options);
    }
    getResultsCategories(sportId, options) {
        return this.feed.getResultsCategories(sportId, options);
    }
    getResultsTournaments(categoryId, options) {
        return this.feed.getResultsTournaments(categoryId, options);
    }
    getResultsSport(id) {
        return this.feed.getResultsSport(id);
    }
    getResultsEvents(tournamentId, options) {
        return this.feed.getResultsEvents(tournamentId, options);
    }
    mergeEventMarkets(marketsMergeInto, marketsMergeFrom) {
        return this.feed.mergeEventMarkets(marketsMergeInto, marketsMergeFrom);
    }
}

var HoursRange;
(function (HoursRange) {
    HoursRange[HoursRange["ALL"] = 0] = "ALL";
    HoursRange[HoursRange["ONE"] = 1] = "ONE";
    HoursRange[HoursRange["TWO"] = 2] = "TWO";
    HoursRange[HoursRange["THREE"] = 3] = "THREE";
    HoursRange[HoursRange["FOUR"] = 4] = "FOUR";
    HoursRange[HoursRange["FIVE"] = 5] = "FIVE";
    HoursRange[HoursRange["SIX"] = 6] = "SIX";
    HoursRange[HoursRange["SEVEN"] = 7] = "SEVEN";
    HoursRange[HoursRange["EIGHT"] = 8] = "EIGHT";
    HoursRange[HoursRange["NINE"] = 9] = "NINE";
    HoursRange[HoursRange["TEN"] = 10] = "TEN";
    HoursRange[HoursRange["ELEVEN"] = 11] = "ELEVEN";
    HoursRange[HoursRange["TWELVE"] = 12] = "TWELVE";
    HoursRange[HoursRange["THIRTEEN"] = 13] = "THIRTEEN";
    HoursRange[HoursRange["FOURTEEN"] = 14] = "FOURTEEN";
    HoursRange[HoursRange["FIFTEEN"] = 15] = "FIFTEEN";
    HoursRange[HoursRange["SIXTEEN"] = 16] = "SIXTEEN";
    HoursRange[HoursRange["SEVENTEEN"] = 17] = "SEVENTEEN";
    HoursRange[HoursRange["EIGHTEEN"] = 18] = "EIGHTEEN";
    HoursRange[HoursRange["NINETEEN"] = 19] = "NINETEEN";
    HoursRange[HoursRange["TWENTY"] = 20] = "TWENTY";
    HoursRange[HoursRange["TWENTY_ONE"] = 21] = "TWENTY_ONE";
    HoursRange[HoursRange["TWENTY_TWO"] = 22] = "TWENTY_TWO";
    HoursRange[HoursRange["TWENTY_THREE"] = 23] = "TWENTY_THREE";
    HoursRange[HoursRange["TWENTY_FOUR"] = 24] = "TWENTY_FOUR";
    HoursRange[HoursRange["FORTY_EIGHT"] = 48] = "FORTY_EIGHT";
})(HoursRange || (HoursRange = {}));

export { ClientsService, FeedStorage, HoursRange, SocketService, SubscriptionStorage, WebsocketClient, getRandomRequestId };
//# sourceMappingURL=index.js.map
